import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  DollarSign,
  MapPin,
  Car,
  Clock,
  AlertTriangle,
  Loader2,
  Eye,
} from "lucide-react";
import { Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  usePricing,
  useCreatePricing,
  useUpdatePricing,
} from "@/api-services/hooks/usePricing";
import {
  useVehicleCategories,
  useCities,
} from "@/api-services/hooks/usePricing";
import {
  BasePricingDTO,
  VehicleCategory,
  CityMaster,
  BasePricing,
} from "@/api-services/hooks/pricingTypes";

const pricingFormSchema = z.object({
  vehicleCategoryId: z.number().min(1, "Vehicle category is required"),
  cityId: z.number().min(1, "City is required"),
  baseFare: z.number().min(0, "Base fare must be at least 0"),
  ratePerKm: z.number().min(0, "Rate per km must be at least 0"),
  ratePerMinute: z.number().min(0, "Rate per minute must be at least 0"),
  minimumFare: z.number().min(0, "Minimum fare must be at least 0"),
  nightCharges: z.number().min(0, "Night charges must be at least 0"),
  tollFeeApplicable: z.boolean(),
});

type PricingFormData = z.infer<typeof pricingFormSchema>;

const AddEditPricingPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Add QueryClient for cache management
  const { pricingId } = useParams<{ pricingId: string }>();
  const isEditing = !!pricingId;
  const pricingIdNum = pricingId ? parseInt(pricingId, 10) : 0;

  // Fetch pricing data for edit
  const { data: pricingData, isLoading: loadingPricing } = usePricing(
    pricingIdNum,
    {
      enabled: isEditing,
      onError: (error) => {
        toast.error(`Failed to load pricing: ${error.message}`);
      },
    }
  );

  // Fetch dropdown data
  const { data: vehicleCategories, isLoading: loadingVehicles } =
    useVehicleCategories();
  const { data: cities, isLoading: loadingCities } = useCities();


  // Create/Update pricing hooks
  const { mutate: createPricing, isPending: isCreating } = useCreatePricing({
    onSuccess: async (data, variables) => {
      toast.success("Pricing rule created successfully!");
      // Invalidate all pricings queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['pricings'] });
      // Navigate after cache invalidation
      navigate("/admin/base-pricing");
    },
    onError: (err, newPricingData) => {
      toast.error(`Failed to create pricing: ${err.message}`);
    },
  });

  const { mutate: updatePricing, isPending: isUpdating } = useUpdatePricing({
    onSuccess: async (data, variables) => {
      toast.success("Pricing rule updated successfully!");
      // Invalidate all pricings queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['pricings'] });
      // Navigate after cache invalidation
      navigate("/admin/base-pricing");
    },
    onError: (error, variables) => {
      toast.error(`Failed to update pricing: ${error.message}`);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    control,
  } = useForm<PricingFormData>({
    resolver: zodResolver(pricingFormSchema),
    defaultValues: {
      tollFeeApplicable: false,
    },
  });

  // Populate form when pricing data is loaded (for edit)
  useEffect(() => {
    if (isEditing && pricingData && !loadingPricing) {
      const pricing = pricingData as BasePricing;
      // Ensure we have valid IDs
      const cityId = pricing.city?.cityId || 0;
      const vehicleCategoryId = pricing.vehicleCategoryId || 0;

      // Reset form with valid data, ensuring all fields are properly set
      reset({
        vehicleCategoryId: vehicleCategoryId,
        cityId: cityId,
        baseFare: pricing.baseFare || 0,
        ratePerKm: pricing.ratePerKm || 0,
        ratePerMinute: pricing.ratePerMinute || 0,
        minimumFare: pricing.minimumFare || 0,
        nightCharges: pricing.nightCharges || 0,
        tollFeeApplicable: pricing.tollFeeApplicable || false,
      });
    }
  }, [pricingData, isEditing, reset, loadingPricing, pricingIdNum]);

  const watchedValues = watch();


  const onSubmit = (data: PricingFormData) => {
    // Find the selected city from the cities array
    const selectedCity = (cities as CityMaster[])?.find(city => city.cityId === data.cityId);

    if (!selectedCity) {
      toast.error("Selected city not found. Please select a valid city.");
      return;
    }

    if (isEditing && pricingIdNum) {
      // For editing, send pricingId and isDeletedValue from existing data
      const pricing = pricingData as BasePricing;
      const editPricingData: BasePricingDTO = {
        pricingId: pricingIdNum, // Include pricingId for updates
        vehicleCategoryId: data.vehicleCategoryId,
        city: selectedCity, // Send complete city object
        baseFare: data.baseFare,
        ratePerKm: data.ratePerKm,
        ratePerMinute: data.ratePerMinute,
        minimumFare: data.minimumFare,
        nightCharges: data.nightCharges,
        tollFeeApplicable: data.tollFeeApplicable,
        isActive: true,
        createdBy: pricing?.createdBy, // Use existing pricingData from component state
        createdDate: pricing?.createdDate, // Use existing pricingData from component state
        isDeletedValue: pricing?.isDeletedValue || false, // Use existing pricingData from component state
        modifiedBy: "admin", // TODO: Get from auth context
      };
      updatePricing({ id: pricingIdNum, data: editPricingData });
    } else {
      // For creating, do NOT send pricingId and set isDeletedValue to false
      const createPricingData: BasePricingDTO = {
        // No pricingId - backend will generate it
        vehicleCategoryId: data.vehicleCategoryId,
        city: selectedCity, // Send complete city object
        baseFare: data.baseFare,
        ratePerKm: data.ratePerKm,
        ratePerMinute: data.ratePerMinute,
        minimumFare: data.minimumFare,
        nightCharges: data.nightCharges,
        tollFeeApplicable: data.tollFeeApplicable,
        isActive: true,
        createdBy: "admin", // TODO: Get from auth context
        isDeletedValue: false, // Required: set to false when creating
        modifiedBy: "admin", // TODO: Get from auth context
      };
      createPricing(createPricingData);
    }
  };

  if (loadingPricing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading pricing details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin/base-pricing")}
          className="shrink-0"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Pricing
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {isEditing ? "Edit Pricing Rule" : "Create New Pricing Rule"}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? "Modify the pricing configuration for a specific location and vehicle type"
              : "Configure base pricing for a specific location and vehicle type"}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Location and Vehicle Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location & Vehicle Configuration
            </CardTitle>
            <CardDescription>
              Select the city and vehicle category for this pricing rule
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Controller
              name="cityId"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="cityId">City *</Label>
                  <Select
                    value={field.value ? field.value.toString() : ""}
                    onValueChange={(value) => {
                      const parsed = parseInt(value, 10);
                      field.onChange(isNaN(parsed) ? 0 : parsed);
                    }}
                    disabled={loadingCities}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a city" />
                    </SelectTrigger>
                    <SelectContent>
                      {(cities as CityMaster[])?.map((city) => (
                        <SelectItem
                          key={city.cityId}
                          value={city.cityId.toString()}
                        >
                          {city.cityName}
                        </SelectItem>
                      )) || []}
                    </SelectContent>
                  </Select>
                  {errors.cityId && (
                    <p className="text-sm text-destructive">
                      {errors.cityId.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="vehicleCategoryId"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="vehicleCategoryId">Vehicle Category *</Label>
                  <Select
                    value={field.value ? field.value.toString() : ""}
                    onValueChange={(value) => {
                      const parsed = parseInt(value, 10);
                      field.onChange(isNaN(parsed) ? 0 : parsed);
                    }}
                    disabled={loadingVehicles}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(vehicleCategories as VehicleCategory[])?.map((vehicle) => (
                        <SelectItem
                          key={vehicle.vehicleCategoryId}
                          value={vehicle.vehicleCategoryId.toString()}
                        >
                          {vehicle.vehicleCategoryName} (Max:{" "}
                          {vehicle.maxPassengerCount} passengers)
                        </SelectItem>
                      )) || []}
                    </SelectContent>
                  </Select>
                  {errors.vehicleCategoryId && (
                    <p className="text-sm text-destructive">
                      {errors.vehicleCategoryId.message}
                    </p>
                  )}
                </div>
              )}
            />
          </CardContent>
        </Card>

        {/* Pricing Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing Configuration
            </CardTitle>
            <CardDescription>
              Set the base fare and rate calculations for this pricing rule
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="baseFare">Base Fare ($) *</Label>
                <Input
                  id="baseFare"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("baseFare", { valueAsNumber: true })}
                />
                {errors.baseFare && (
                  <p className="text-sm text-destructive">
                    {errors.baseFare.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ratePerKm">Rate per KM (USD) *</Label>
                <Input
                  id="ratePerKm"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("ratePerKm", { valueAsNumber: true })}
                />
                {errors.ratePerKm && (
                  <p className="text-sm text-destructive">
                    {errors.ratePerKm.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ratePerMinute">Rate per Minute ($) *</Label>
                <Input
                  id="ratePerMinute"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("ratePerMinute", { valueAsNumber: true })}
                />
                {errors.ratePerMinute && (
                  <p className="text-sm text-destructive">
                    {errors.ratePerMinute.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="minimumFare">Minimum Fare (USD) *</Label>
                <Input
                  id="minimumFare"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("minimumFare", { valueAsNumber: true })}
                />
                {errors.minimumFare && (
                  <p className="text-sm text-destructive">
                    {errors.minimumFare.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nightCharges">Night Charges (USD) *</Label>
                <Input
                  id="nightCharges"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("nightCharges", { valueAsNumber: true })}
                />
                {errors.nightCharges && (
                  <p className="text-sm text-destructive">
                    {errors.nightCharges.message}
                  </p>
                )}
              </div>

              <Controller
              name="tollFeeApplicable"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="tollFeeApplicable">Toll Fee Settings</Label>
                  <div className="flex items-center space-x-2 mt-4">
                    <Checkbox
                      id="tollFeeApplicable"
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                    <Label
                      htmlFor="tollFeeApplicable"
                      className="text-sm font-normal"
                    >
                      Toll fees are applicable for this route
                    </Label>
                  </div>
                </div>
              )}
            />
            </div>
          </CardContent>
        </Card>

        {/* Pricing Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Pricing Preview
            </CardTitle>
            <CardDescription>
              Preview how the pricing will be calculated for a typical ride
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-muted/30 rounded-lg">
              <div>
                <h4 className="font-semibold mb-3 text-sm">
                  Example Ride Calculation:
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Fare:</span>
                    <span className="font-medium">
                      {'$' + (watchedValues.baseFare?.toFixed(2) || "0.00")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      5 km × {'$' + (watchedValues.ratePerKm?.toFixed(2) || "0.00")}
                      /km:
                    </span>
                    <span className="font-medium">
                      {'$' + ((watchedValues.ratePerKm || 0) * 5).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      15 min × {'$' + (watchedValues.ratePerMinute?.toFixed(2) || "0.00")}/min:
                    </span>
                    <span className="font-medium">
                      {'$' + ((watchedValues.ratePerMinute || 0) * 15).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Night Charges:</span>
                    <span className="font-medium">
                      {'$' + (watchedValues.nightCharges?.toFixed(2) || "0.00")}
                    </span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Subtotal:</span>
                      <span>
                        {'$' +
                          Math.max(
                            watchedValues.minimumFare || 0,
                            (watchedValues.baseFare || 0) +
                              (watchedValues.ratePerKm || 0) * 5 +
                              (watchedValues.ratePerMinute || 0) * 15 +
                              (watchedValues.nightCharges || 0)
                          ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold mb-2 text-sm text-blue-800">
                  <AlertTriangle className="inline w-4 h-4 mr-1" />
                  Important Notes:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Minimum fare guarantees driver earnings</li>
                  <li>• Night charges apply during specific hours</li>
                  <li>• Toll fees are added separately when applicable</li>
                  <li>• Rates are calculated based on actual route and time</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/base-pricing")}
            disabled={isCreating || isUpdating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isCreating || isUpdating}
            className="bg-gradient-primary hover:opacity-90"
          >
            {isCreating || isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? "Updating..." : "Creating..."}
              </>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? "Update Pricing Rule" : "Create Pricing Rule"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditPricingPage;
